import { t } from "@lingui/core/macro";
import { addToast } from "@heroui/react";
import dayjs from "dayjs";
import { api } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import type { GetTimeOff } from "@/server-types";
import {
  type PendingTimeOffDrop,
  useCalendarStore,
} from "../../store/useCalendarStore";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";
import {
  buildTimeOffMoveChanges,
  movesOffSeries,
  type TimeOffDrop,
  type TimeOffScope,
} from "./timeOffForm";

export type { TimeOffDrop } from "./timeOffForm";
export { movesOffSeries } from "./timeOffForm";

export function useTimeOffDrag() {
  const queryClient = useQueryClient();
  const setPendingTimeOffDrop = useCalendarStore(
    (state) => state.setPendingTimeOffDrop,
  );
  const { mutateAsync: update } = api.timeoff.useUpdateTimeOffOccurrence();

  function moveRow(
    data: GetTimeOff["response"] | undefined,
    drop: PendingTimeOffDrop,
  ) {
    if (data == null) return data;

    return {
      ...data,
      timeoffs: data.timeoffs.map((row) => {
        if (row.id !== drop.id || row.source !== drop.source) return row;

        const durationMins = dayjs(row.timeTo).diff(
          dayjs(row.timeFrom),
          "minute",
        );
        const timeFrom = dayjs(`${drop.date}T${drop.startTime}`);

        return {
          ...row,
          timeFrom: timeFrom.format("YYYY-MM-DDTHH:mm"),
          timeTo: timeFrom
            .add(durationMins, "minute")
            .format("YYYY-MM-DDTHH:mm"),
          userId: drop.source === "staff" ? drop.userId : row.userId,
          resourceId:
            drop.source === "resource" ? drop.resourceId : row.resourceId,
        };
      }),
    };
  }

  /**
   * Moves the row before the request goes out, in both places that hold it.
   * The columns render from the store, which `CalendarPage` fills from the
   * query in an effect — so patching the cache alone still leaves one painted
   * frame at the old position, which is the snap-back. The store is written
   * here directly to close that gap, and the cache with it so the next read
   * does not hand the stale row straight back.
   */
  function applyLocally(drop: PendingTimeOffDrop) {
    const { timeoffs, setTimeoffs } = useCalendarStore.getState();
    setTimeoffs(moveRow(timeoffs, drop));

    queryClient.setQueriesData<GetTimeOff["response"]>(
      { queryKey: ["timeoff"] },
      (data) => moveRow(data, drop),
    );
  }

  async function commit(drop: PendingTimeOffDrop, scope: TimeOffScope) {
    applyLocally(drop);

    try {
      await update({
        target: drop.source,
        id: drop.id,
        scope,
        ...buildTimeOffMoveChanges(drop, scope),
      });
    } catch (error) {
      // The mutation has already told the user; swallowing here is what stops
      // a refused drop becoming an unhandled rejection, since nothing awaits
      // this on the drag path.
      sentryLog.error("Time off drag failed", {
        ...describeErrorForLog(error),
        timeOffId: drop.id,
        source: drop.source,
        scope,
      });
      return;
    } finally {
      // Also on failure: the optimistic move has to be undone, and the server's
      // rows are the only thing that can say where the block really belongs.
      await queryClient.invalidateQueries({ queryKey: ["timeoff"] });
    }

    addToast({ title: t`Odsotnost premaknjena`, color: "success" });
  }

  /**
   * A standalone occurrence is written straight away — the drag itself is the
   * confirmation. One belonging to a series waits for a scope, because the same
   * gesture could otherwise rewrite every occurrence after it.
   */
  async function handleDrop(drop: TimeOffDrop) {
    // Nothing awaits this on the drag path, so anything thrown outside
    // `commit`'s own catch — the local move, the store write — would surface as
    // an unhandled rejection rather than a report.
    try {
      await resolveDrop(drop);
    } catch (error) {
      sentryLog.error("Time off drop failed", {
        ...describeErrorForLog(error),
        timeOffId: drop.id,
        source: drop.source,
      });
      await queryClient.invalidateQueries({ queryKey: ["timeoff"] });
    }
  }

  async function resolveDrop(drop: TimeOffDrop) {
    // Staff and resource time offs live in separate tables, and the endpoint
    // moves a row within its own. A drop onto the other kind of column would
    // otherwise write only the day and the time and look like a half-move.
    const landedOnOwnKind =
      drop.source === "staff" ? drop.userId != null : drop.resourceId != null;
    if (!landedOnOwnKind) return;

    const pending: PendingTimeOffDrop = {
      id: drop.id,
      source: drop.source,
      date: drop.date,
      startTime: drop.startTime,
      userId: drop.userId,
      resourceId: drop.resourceId,
      allowFollowing: !movesOffSeries(drop),
    };

    if (drop.seriesId == null) {
      await commit(pending, "ONLY_THIS");
      return;
    }

    // Moved before the scope is known, for the same reason a standalone drop
    // is: leaving it until the dialog is answered puts the block back where it
    // was dragged from and hops it a second time. `cancelDrop` is what undoes
    // it if the dialog is dismissed.
    applyLocally(pending);
    setPendingTimeOffDrop(pending);
  }

  /** Dismissing the scope dialog writes nothing, so the server's rows are what
   * put the block back. */
  async function cancelDrop() {
    setPendingTimeOffDrop(null);
    await queryClient.invalidateQueries({ queryKey: ["timeoff"] });
  }

  return { handleDrop, commit, cancelDrop };
}
